import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { FadeUp, SectionHeading, Counter } from "@/components/Reveal";
import { WIN_GRAPH, WIN_TOTALS, WIN_RATE } from "@/data/mp";
import { PARTIES } from "@/data/services";

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const c = payload.find((p) => p.dataKey === "campaigns")?.value ?? 0;
  const w = payload.find((p) => p.dataKey === "won")?.value ?? 0;
  return (
    <div className="rounded-lg border border-white/10 bg-ink/95 px-4 py-3 font-mono text-xs text-paper shadow-xl">
      <p className="text-zinc-500">{label}</p>
      <p className="mt-1">Campaigns: <span className="text-ocean-glow">{c}</span></p>
      <p>Won: <span className="text-brand">{w}</span></p>
    </div>
  );
}

export default function WinningGraph() {
  return (
    <section className="border-t border-white/10 bg-coal/40" data-testid="winning-graph-section">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading overline="Winning Graph — जीत का ग्राफ" lines={["Our record,", "year by year."]} testId="winning-heading" />
            <FadeUp delay={0.15}>
              <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/10 pt-8 lg:grid-cols-1 lg:gap-8">
                <div data-testid="win-rate-stat">
                  <Counter to={WIN_RATE} suffix="%" className="font-display text-5xl font-bold text-brand sm:text-6xl" />
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Win rate</p>
                </div>
                <div data-testid="campaigns-total-stat">
                  <Counter to={WIN_TOTALS.campaigns} className="font-display text-3xl font-bold text-paper sm:text-4xl" />
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Campaigns since 2019</p>
                </div>
                <div data-testid="won-total-stat">
                  <Counter to={WIN_TOTALS.won} className="font-display text-3xl font-bold text-saffron sm:text-4xl" />
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Seats & posts won</p>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp className="lg:col-span-8" delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-ink/60 p-6 lg:p-8" data-testid="winning-chart">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Campaigns handled vs won</p>
                <div className="flex gap-5 font-mono text-[10px] uppercase tracking-[0.2em]">
                  <span className="flex items-center gap-2 text-zinc-400"><i className="h-2 w-2 rounded-full bg-ocean" /> Campaigns</span>
                  <span className="flex items-center gap-2 text-zinc-400"><i className="h-2 w-2 rounded-full bg-brand" /> Won</span>
                </div>
              </div>
              <div className="mt-6 h-[300px] w-full sm:h-[340px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={300}>
                  <AreaChart data={WIN_GRAPH} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gCamp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2F6BFF" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#2F6BFF" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gWon" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF3D8A" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#FF3D8A" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="year" stroke="rgba(255,255,255,0.25)" tick={{ fill: "#71717a", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                    <YAxis stroke="rgba(255,255,255,0.25)" tick={{ fill: "#71717a", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<Tip />} cursor={{ stroke: "rgba(255,255,255,0.15)" }} />
                    <Area type="monotone" dataKey="campaigns" stroke="#2F6BFF" strokeWidth={2} fill="url(#gCamp)" animationDuration={1600} />
                    <Area type="monotone" dataKey="won" stroke="#FF3D8A" strokeWidth={2.5} fill="url(#gWon)" animationDuration={1800} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="parties-strip">
              {PARTIES.map((p) => (
                <div key={p.short} data-testid={`party-${p.short.toLowerCase()}`} className="rounded-xl border border-white/10 bg-coal/60 px-4 py-4 transition-colors duration-300 hover:border-white/25">
                  <p className="font-display text-2xl font-bold tracking-tight" style={{ color: p.color }}>{p.short}</p>
                  <p className="mt-1 text-xs text-zinc-500">{p.name}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              Trusted across the aisle — we have run campaigns for BJP and Congress candidates, independents and regional alliances.
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
